import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, CalendarClock, ChevronRight, Clock3, Phone, RefreshCw,
  Search, UserRoundCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, asItems, asObject, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  count, EntityLink, FilterActions, FilterField, formatDateTime, labelize, officeLink,
  options, PropertyThumb, propertyLink, SearchField, StatusPill, SummaryCards,
  toDateTimeLocalValue, userLink, WorkbenchEmpty, WorkbenchError, WorkbenchHeader,
  WorkbenchLoading, WorkbenchPagination,
} from "@/components/admin/AdminWorkbenchUi";
import { SiteVisitDetail } from "@/pages/dashboard/AdminSiteVisitWorkbench";

const PAGE_SIZE = 20;
const KEYS = ["q", "queue", "status", "agent_id", "office_id", "timing", "sort"];
const EMPTY = { q: "", queue: "ATTENTION", status: "", agent_id: "", office_id: "", timing: "", sort: "schedule" };
const read = params => Object.fromEntries(KEYS.map(key => [key, params.get(key) || EMPTY[key]]));

/** Focused flat queue for assignment and status work; the Workbench stays property-grouped. */
export default function AdminSiteVisitAssignments() {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const selected = params.get("select") || "";
  const requestedPage = Number(params.get("page") || 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? Math.min(requestedPage, 1_000_000) : 1;
  const [draft, setDraft] = useState(() => read(params));
  const [data, setData] = useState({ items: [], summary: {}, page: 1, limit: PAGE_SIZE, total: 0 });
  const [filters, setFilters] = useState({});
  const [detail, setDetail] = useState(null);
  const [assignment, setAssignment] = useState({ agent_id: "", office_id: "", visit_at: "", note: "" });
  const [statusNote, setStatusNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const appliedQuery = useMemo(() => {
    const source = new URLSearchParams(query);
    const next = new URLSearchParams();
    KEYS.forEach(key => source.get(key) && next.set(key, source.get(key)));
    return next.toString();
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const request = Object.fromEntries(new URLSearchParams(appliedQuery).entries());
      request.page = page;
      request.limit = PAGE_SIZE;
      const response = await api.get("/v1/admin/site-visits-workbench/assignments", { params: request });
      const payload = asObject(response.data);
      setData({ ...payload, items: asItems(payload) });
    } catch (requestError) {
      setData({ items: [], summary: {}, page: 1, limit: PAGE_SIZE, total: 0 });
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, page, reloadKey]);

  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const response = await api.get(`/v1/admin/site-visits-workbench/${selected}`);
      const payload = asObject(response.data);
      const visit = payload.visit || {};
      setDetail(payload);
      setAssignment({
        agent_id: visit.agent_id || "",
        office_id: visit.office_id || "",
        visit_at: toDateTimeLocalValue(visit.visit_at),
        note: "",
      });
    } catch (requestError) {
      toast.error(formatApiError(requestError));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [selected]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { setDraft(read(new URLSearchParams(query))); }, [query]);
  useEffect(() => {
    let current = true;
    api.get("/v1/admin/site-visits-workbench/filters")
      .then(response => current && setFilters(asObject(response.data)))
      .catch((requestError) => {
        if (current) {
          setFilters({});
          toast.error(`Site Visit assignment filters could not be loaded: ${formatApiError(requestError) || "Request failed."}`);
        }
      });
    return () => { current = false; };
  }, []);

  const select = id => {
    const next = new URLSearchParams(params);
    id ? next.set("select", id) : next.delete("select");
    setParams(next);
  };
  const apply = () => {
    const next = new URLSearchParams();
    KEYS.forEach(key => draft[key] && next.set(key, draft[key]));
    setParams(next);
  };
  const reset = () => {
    setDraft(EMPTY);
    setParams(new URLSearchParams());
  };
  const changePage = nextPage => {
    const next = new URLSearchParams(params);
    next.set("page", String(nextPage));
    next.delete("select");
    setParams(next);
  };
  const mutate = async (path, body, success) => {
    setSaving(true);
    try {
      await api.post(path, body);
      toast.success(success);
      setStatusNote("");
      setAssignment(current => ({ ...current, note: "" }));
      setReloadKey(value => value + 1);
      await loadDetail();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const summary = data.summary || {};
  const cards = [
    { label: "Queue results", value: data.total, icon: Search },
    { label: "Unassigned", value: summary.unassigned, icon: UserRoundCheck, tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
    { label: "Overdue", value: summary.overdue, icon: AlertTriangle, tone: "bg-destructive/10 text-destructive" },
    { label: "Today", value: summary.today, icon: CalendarClock },
    { label: "In progress", value: summary.in_progress, icon: Clock3 },
    { label: "Upcoming", value: summary.upcoming, icon: CalendarClock, tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  ];

  return <div className="space-y-5" data-testid="admin-site-visit-assignments">
    <WorkbenchHeader eyebrow="Site Visit Management" title="Site Visit Assignment & Management" description="Work from a flat priority queue to assign agents, reschedule visits and apply safe admin status changes. Start and completion remain protected by verification." actions={<>
      <Button asChild variant="ghost"><Link to="/dashboard/admin/site-visits" title="Open Site Visits overview">Overview</Link></Button><Button asChild variant="outline"><Link to="/dashboard/admin/site-visits/workbench" title="Open the property-grouped Site Visit Workbench"><Search className="h-4 w-4"/>Open workbench</Link></Button>
      <Button variant="outline" onClick={() => setReloadKey(value => value + 1)} disabled={loading} title="Refresh Site Visit assignment queue"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}/>Refresh</Button>
    </>}/>
    <SummaryCards items={cards} loading={loading}/>
    <form onSubmit={event => { event.preventDefault(); apply(); }} className="rounded-2xl border bg-card p-4 shadow-sm" aria-label="Site Visit assignment filters"><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-8"><div className="sm:col-span-2 xl:col-span-2"><SearchField value={draft.q} onChange={value => setDraft(current => ({ ...current, q: value }))} placeholder="Buyer, phone, property or visit ID"/></div><FilterField label="Queue" value={draft.queue} onChange={value => setDraft(current => ({ ...current, queue: value }))} choices={options([
      { value: "ATTENTION", label: "Needs attention" }, { value: "UNASSIGNED", label: "Unassigned" },
      { value: "OVERDUE", label: "Overdue" }, { value: "UPCOMING", label: "Upcoming" },
      { value: "ACTIVE", label: "All active" }, { value: "ALL", label: "All records" },
    ], "Default: needs attention")}/><FilterField label="Status" value={draft.status} onChange={value => setDraft(current => ({ ...current, status: value }))} choices={options(filters.statuses, "All statuses")}/><FilterField label="Agent" value={draft.agent_id} onChange={value => setDraft(current => ({ ...current, agent_id: value }))} choices={options(filters.agents, "All agents")}/><FilterField label="Office" value={draft.office_id} onChange={value => setDraft(current => ({ ...current, office_id: value }))} choices={options(filters.offices, "All offices")}/><FilterField label="Timing" value={draft.timing} onChange={value => setDraft(current => ({ ...current, timing: value }))} choices={options([{value:"TODAY",label:"Today"},{value:"UPCOMING",label:"Upcoming"},{value:"OVERDUE",label:"Overdue"}],"Any timing")}/><FilterActions onApply={apply} onReset={reset} loading={loading}/></div></form>
    {error ? <WorkbenchError message={error} onRetry={() => setReloadKey(value => value + 1)}/> : <section className="overflow-hidden rounded-2xl border bg-card shadow-sm"><div className="grid min-h-[560px] xl:h-[72vh] xl:max-h-[780px] xl:grid-cols-[minmax(0,1.45fr)_minmax(370px,.8fr)]">
      <div className={`${selected ? "hidden xl:block" : "block"} min-w-0 xl:overflow-y-auto`}><div className="border-b p-4"><h2 className="font-display text-lg font-black">Management queue</h2><p className="mt-1 text-xs text-muted-foreground">{loading ? "Loading visits…" : `${count(data.total)} visits match this queue`}</p></div>{loading ? <WorkbenchLoading/> : data.items.length === 0 ? <WorkbenchEmpty title="No Site Visits in this queue" detail="Choose another queue or clear filters."/> : <div className="divide-y">{data.items.map(visit => <VisitQueueRow key={visit.site_visit_id} visit={visit} selected={selected === visit.site_visit_id} onSelect={() => select(visit.site_visit_id)}/>)}</div>} {!loading && <WorkbenchPagination page={page} limit={PAGE_SIZE} total={data.total} onPage={changePage}/>}</div>
      <aside className={`${selected ? "block" : "hidden xl:block"} min-w-0 bg-muted/15 xl:border-l xl:overflow-y-auto`}>{!selected ? <div className="flex h-full items-center justify-center p-10 text-center text-sm text-muted-foreground">Select a Site Visit to assign, reschedule or change its admin-managed status.</div> : detailLoading ? <WorkbenchLoading/> : detail ? <SiteVisitDetail data={detail} filters={filters} assignment={assignment} setAssignment={setAssignment} statusNote={statusNote} setStatusNote={setStatusNote} saving={saving} onBack={() => select("")} onAssign={() => mutate(`/v1/admin/site-visits-workbench/${selected}/assign`, { ...assignment, visit_at: assignment.visit_at ? new Date(assignment.visit_at).toISOString() : null }, "Site Visit schedule updated")} onStatus={status => mutate(`/v1/admin/site-visits-workbench/${selected}/transition`, { status, note: statusNote }, `Site Visit marked ${labelize(status)}`)}/> : <WorkbenchEmpty title="Site Visit unavailable"/>}</aside>
    </div></section>}
  </div>;
}

function VisitQueueRow({ visit, selected, onSelect }) {
  return <article className={`grid gap-3 p-4 text-left transition-colors duration-200 ease-out hover:bg-muted/40 md:grid-cols-[minmax(250px,1.2fr)_minmax(180px,.8fr)_minmax(170px,.7fr)_auto] ${selected ? "bg-primary/5" : ""}`}>
    <div className="flex min-w-0 gap-3"><PropertyThumb src={visit.thumbnail_url} alt={visit.property_title}/><div className="min-w-0"><EntityLink to={visit.property_id && propertyLink(visit.property_id)} onClick={event => event.stopPropagation()} className="block truncate">{visit.property_title || "Unlinked property"}</EntityLink><p className="mt-1 truncate text-xs text-muted-foreground">{visit.property_code || "No property code"}</p><div className="mt-2 flex gap-1"><StatusPill value={visit.status}/>{visit.overdue && <StatusPill value="OVERDUE"/>}</div></div></div>
    <div><EntityLink to={visit.buyer_user_id && userLink(visit.buyer_user_id)} onClick={event => event.stopPropagation()}>{visit.buyer_name || "Unknown buyer"}</EntityLink><p className="mt-1 text-xs"><Phone className="mr-1 inline h-3.5 w-3.5"/>{visit.buyer_phone ? <a href={`tel:${visit.buyer_phone}`} onClick={event => event.stopPropagation()} title="Call buyer" className="hover:underline">{visit.buyer_phone}</a> : "No phone"}</p></div>
    <div><EntityLink to={visit.agent_id && userLink(visit.agent_id)} onClick={event => event.stopPropagation()}>{visit.agent_name || "Unassigned"}</EntityLink><p className="mt-1 text-xs"><EntityLink to={visit.office_id && officeLink(visit.office_id)} onClick={event => event.stopPropagation()}>{visit.office_name || "No office"}</EntityLink></p><p className="mt-2 text-xs text-muted-foreground">{formatDateTime(visit.visit_at)}</p></div>
    <div className="flex items-center justify-end"><Button type="button" size="sm" variant="ghost" onClick={event => { event.stopPropagation(); onSelect(); }} title={`Manage Site Visit for ${visit.buyer_name || "buyer"}`}>Manage<ChevronRight className="h-4 w-4"/></Button></div>
  </article>;
}
